import {
  DeleteOutlined,
  FilePdfOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, ConfigProvider, message, Space, Upload } from "antd";
import type { UploadProps } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { taxRegistrationApi, uploadTaxRegistrationAttachment } from "./api";
import { formatFileSize } from "./formHelpers";
import {
  AttachmentEmpty,
  AttachmentHint,
  AttachmentName,
  AttachmentRow,
  AttachmentSize,
  AttachmentUploadRow,
} from "./styles";
import type { TaxRegistration, TaxRegistrationAttachment } from "./types";

/**
 * The limits kontax-backend enforces when it reads the uploaded object, and
 * kontax-neric-api again before ERiC. They are repeated here so the operator is
 * told which limit was hit before a 10 MB file crosses the wire, never to
 * replace the server-side check.
 */
export const MAX_ATTACHMENTS = 20;
export const MAX_ATTACHMENT_MB = 10;
const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_MB * 1024 * 1024;

export interface TaxRegistrationAttachmentsProps {
  /** Absent until the record has been saved once. */
  registrationId?: string;
  attachments: TaxRegistrationAttachment[];
  readOnly?: boolean;
  /**
   * Saves the draft and resolves the id of the stored record. A new record has
   * no id, and there is nothing to presign an upload against until it does.
   */
  ensureRegistrationId: () => Promise<string>;
  /** The registration as it stands after an upload or a removal. */
  onChange: (taxRegistration: TaxRegistration) => void;
}

const isPdf = (file: File): boolean =>
  /\.pdf$/i.test(file.name) && (!file.type || file.type === "application/pdf");

/**
 * The Anlagen of a KapG questionnaire: the Gesellschaftsvertrag and whatever
 * else is filed with it, transmitted in the ELSTER `Anhaenge` block (Zeile 227,
 * SMT-1341).
 *
 * The list is driven entirely by the record the backend answers with. Nothing
 * is kept locally between an upload and the next render, so what is on screen
 * is what would be transmitted, including the file name after the backend
 * sanitised it.
 */
export const TaxRegistrationAttachments = ({
  registrationId,
  attachments,
  readOnly = false,
  ensureRegistrationId,
  onChange,
}: TaxRegistrationAttachmentsProps) => {
  const { t } = useTranslation("TaxRegistration");
  const [pendingChanges, setPendingChanges] = useState(0);
  /*
   * Uploads run one after another, and the id of a record created by the first
   * of them is read from a ref: the `registrationId` prop cannot have arrived
   * yet while the queue is still draining, and a second `ensureRegistrationId`
   * would create a second record for the same Mandant.
   */
  const registrationIdRef = useRef(registrationId);
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  /*
   * Every prop change, `undefined` included: a record that goes from saved back
   * to unsaved must forget its id, or the next upload would be attached to the
   * registration that was on screen before.
   */
  useEffect(() => {
    registrationIdRef.current = registrationId;
  }, [registrationId]);

  const resolveRegistrationId = async (): Promise<string> => {
    if (registrationIdRef.current) return registrationIdRef.current;

    const id = await ensureRegistrationId();
    registrationIdRef.current = id;
    return id;
  };

  /** Why a file cannot be uploaded at all, whatever else is in the drop. */
  const getFileRejection = (file: File): string | undefined => {
    if (!isPdf(file)) {
      return String(t("kapg.attachments.invalidType", { fileName: file.name }));
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      return String(
        t("kapg.attachments.tooLarge", {
          fileName: file.name,
          limit: MAX_ATTACHMENT_MB,
        })
      );
    }
    return undefined;
  };

  const getRejection = (file: File, fileList: File[]): string | undefined => {
    const fileRejection = getFileRejection(file);
    if (fileRejection) return fileRejection;

    // The files of one drop are validated against the list they would join, so
    // dropping five on a record that already holds eighteen refuses the last
    // three rather than letting the backend refuse them one by one. Only the
    // files before it that will be uploaded take a place in that list.
    const position = fileList
      .slice(0, Math.max(fileList.indexOf(file), 0))
      .filter((earlier) => getFileRejection(earlier) === undefined).length;
    if (attachments.length + position >= MAX_ATTACHMENTS) {
      return String(t("kapg.attachments.tooMany", { limit: MAX_ATTACHMENTS }));
    }

    return undefined;
  };

  const beforeUpload: UploadProps["beforeUpload"] = (file, fileList) => {
    const rejection = getRejection(file as File, fileList as File[]);
    if (rejection) {
      message.error(rejection);
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  /*
   * Uploads and removals share one queue. Each of them answers with the whole
   * record, so the answers are applied in the order the changes were made; a
   * removal that overtook a running upload would otherwise be undone on screen
   * by the upload's older snapshot.
   */
  const enqueue = (
    task: () => Promise<void>,
    describeError: (error: string) => string
  ) => {
    setPendingChanges((pending) => pending + 1);
    queue.current = queue.current
      .then(task)
      .catch((error: unknown) => {
        message.error(
          describeError(error instanceof Error ? error.message : String(error))
        );
      })
      .finally(() => setPendingChanges((pending) => pending - 1));
  };

  const customRequest: UploadProps["customRequest"] = ({ file }) => {
    enqueue(
      async () => {
        const id = await resolveRegistrationId();
        onChange(await uploadTaxRegistrationAttachment(id, file as File));
      },
      (error) =>
        String(
          t("kapg.attachments.uploadFailed", {
            fileName: (file as File).name,
            error,
          })
        )
    );
  };

  const openAttachment = async (attachmentId: string) => {
    const id = registrationIdRef.current;
    if (!id) return;

    // The tab is opened before the request, as the submitted documents table
    // does: a tab opened in the promise is a popup the browser blocks.
    const openedWindow = window.open();
    try {
      const { url } = await taxRegistrationApi.getAttachmentUrl(
        id,
        attachmentId
      );
      if (openedWindow) {
        openedWindow.location.href = url;
      }
    } catch (error) {
      openedWindow?.close();
      message.error(
        String(
          t("kapg.attachments.openFailed", {
            error: error instanceof Error ? error.message : String(error),
          })
        )
      );
    }
  };

  const removeAttachment = (attachmentId: string) => {
    enqueue(
      async () => {
        const id = registrationIdRef.current;
        if (!id) return;

        onChange(await taxRegistrationApi.deleteAttachment(id, attachmentId));
      },
      (error) => String(t("kapg.attachments.removeFailed", { error }))
    );
  };

  /*
   * The KapG form disables the whole antd `Form` for a transmitted record, and
   * that disables every antd control inside it, including these actions. The
   * attachments are not answers to the questionnaire: the files stay readable
   * after the transmission, which is what AC 5 of SMT-1341 asks for, so this
   * block opts out of the inherited disabled state and hides what must not be
   * done instead (the upload and the removal).
   */
  return (
    <ConfigProvider componentDisabled={false}>
      <div data-test="taxRegistrationAttachments">
        {!readOnly && (
          <AttachmentUploadRow>
            <span data-test="attachmentUpload">
              <Upload
                accept=".pdf,application/pdf"
                beforeUpload={beforeUpload}
                customRequest={customRequest}
                multiple
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />} loading={pendingChanges > 0}>
                  {t("kapg.attachments.upload")}
                </Button>
              </Upload>
            </span>
            <AttachmentHint>
              {t("kapg.attachments.hint", {
                files: MAX_ATTACHMENTS,
                limit: MAX_ATTACHMENT_MB,
              })}
            </AttachmentHint>
          </AttachmentUploadRow>
        )}
        {attachments.length === 0 ? (
          <AttachmentEmpty>{t("kapg.attachments.empty")}</AttachmentEmpty>
        ) : (
          attachments.map((attachment) => (
            <AttachmentRow data-test="attachmentRow" key={attachment.id}>
              <AttachmentName>
                <FilePdfOutlined /> {attachment.fileName}
                <AttachmentSize>
                  {formatFileSize(attachment.size)}
                </AttachmentSize>
              </AttachmentName>
              <Space>
                <Button
                  data-test="attachmentOpen"
                  onClick={() => openAttachment(attachment.id)}
                  type="link"
                >
                  {t("kapg.attachments.open")}
                </Button>
                {!readOnly && (
                  <Button
                    data-test="attachmentRemove"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeAttachment(attachment.id)}
                    type="link"
                  >
                    {t("kapg.attachments.remove")}
                  </Button>
                )}
              </Space>
            </AttachmentRow>
          ))
        )}
      </div>
    </ConfigProvider>
  );
};
